import { desktop } from './desktop';
import type { UserSettings } from './settings';
import { createStore, useStore } from './store';

export interface DeviceLists {
  audioInputs: MediaDeviceInfo[];
  audioOutputs: MediaDeviceInfo[];
  videoInputs: MediaDeviceInfo[];
  /** Labels are hidden by the browser until a permission has been granted once. */
  labelsAvailable: boolean;
}

export const devicesStore = createStore<DeviceLists>({ audioInputs: [], audioOutputs: [], videoInputs: [], labelsAvailable: false });

export const supports = {
  media: typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
  screenShare: typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia,
  outputSelection: typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype,
};

export async function refreshDevices(): Promise<DeviceLists> {
  if (!navigator.mediaDevices?.enumerateDevices) return devicesStore.get();
  const all = await navigator.mediaDevices.enumerateDevices();
  const lists: DeviceLists = {
    audioInputs: all.filter((d) => d.kind === 'audioinput'),
    audioOutputs: all.filter((d) => d.kind === 'audiooutput'),
    videoInputs: all.filter((d) => d.kind === 'videoinput'),
    labelsAvailable: all.some((d) => d.label),
  };
  devicesStore.set(lists);
  return lists;
}

if (navigator.mediaDevices?.addEventListener) {
  navigator.mediaDevices.addEventListener('devicechange', () => void refreshDevices());
}

export function useDevices(): DeviceLists {
  return useStore(devicesStore);
}

export function deviceLabel(d: MediaDeviceInfo, index: number, fallback: string): string {
  if (d.deviceId === 'default') return d.label ? `Predeterminado (${d.label.replace(/^Default - |^Predeterminado - /, '')})` : 'Predeterminado del sistema';
  return d.label || `${fallback} ${index + 1}`;
}

function audioConstraints(s: UserSettings): MediaTrackConstraints {
  return {
    deviceId: s.audioInputId && s.audioInputId !== 'default' ? { exact: s.audioInputId } : undefined,
    echoCancellation: s.echoCancellation,
    noiseSuppression: s.noiseSuppression,
    autoGainControl: s.autoGainControl,
  };
}

function videoConstraints(s: UserSettings): MediaTrackConstraints {
  return {
    deviceId: s.videoInputId ? { exact: s.videoInputId } : undefined,
    width: { ideal: s.hdVideo ? 1280 : 640 },
    height: { ideal: s.hdVideo ? 720 : 360 },
    frameRate: { ideal: s.hdVideo ? 30 : 24 },
  };
}

/** Retries without the saved deviceId when that device was unplugged. */
async function acquire(constraints: MediaStreamConstraints, kind: 'audio' | 'video'): Promise<MediaStreamTrack> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    return kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
  } catch (err) {
    const c = constraints[kind];
    if ((err as DOMException).name === 'OverconstrainedError' && typeof c === 'object' && c.deviceId) {
      const stream = await navigator.mediaDevices.getUserMedia({ [kind]: { ...c, deviceId: undefined } });
      return kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
    }
    throw err;
  }
}

export async function getMicrophoneTrack(s: UserSettings): Promise<MediaStreamTrack> {
  const track = await acquire({ audio: audioConstraints(s) }, 'audio');
  void refreshDevices();
  return track;
}

export async function getCameraTrack(s: UserSettings): Promise<MediaStreamTrack> {
  const track = await acquire({ video: videoConstraints(s) }, 'video');
  track.contentHint = 'motion';
  void refreshDevices();
  return track;
}

export type ShareSurface = 'monitor' | 'window' | 'browser';

export interface ScreenShareOptions {
  /** Which list the browser's picker opens on (the person can still switch). */
  surface: ShareSurface;
  /** Capture the sound of the tab / computer. */
  audio: boolean;
  /** Smooth motion (videos) instead of sharp text (documents). */
  optimizeVideo: boolean;
  /** Desktop app only: the screen or window picked in Nexo's own picker. */
  source?: { id: string; name: string };
}

/**
 * Asks the browser for a screen, window or tab. The browser shows its own picker with live
 * thumbnails; web pages are not allowed to list other windows themselves.
 * Option names follow the Screen Capture spec; browsers ignore the ones they do not know.
 */
export async function getScreenStream(opts: ScreenShareOptions): Promise<MediaStream> {
  // In the desktop app the person already chose in Nexo's picker; the app grants that source.
  if (desktop && opts.source) await desktop.chooseSource(opts.source.id, opts.audio, opts.source.name);
  const constraints = {
    video: { displaySurface: opts.surface, frameRate: { ideal: opts.optimizeVideo ? 30 : 15, max: 30 } },
    audio: opts.audio ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false, suppressLocalAudioPlayback: false } : false,
    systemAudio: opts.audio ? 'include' : 'exclude',
    windowAudio: opts.audio ? 'system' : 'exclude',
    restrictOwnAudio: true, // do not send the meeting's own sound back (echo)
    selfBrowserSurface: 'exclude', // hide the Nexo tab itself from the list
    surfaceSwitching: 'include', // lets the person switch tabs without stopping
    monitorTypeSurfaces: 'include',
  };
  const stream = await navigator.mediaDevices.getDisplayMedia(constraints as DisplayMediaStreamOptions);
  const track = stream.getVideoTracks()[0];
  if (track) track.contentHint = opts.optimizeVideo ? 'motion' : 'detail';
  return stream;
}

export function surfaceOf(stream: MediaStream | null): ShareSurface | null {
  const s = stream?.getVideoTracks()[0]?.getSettings() as (MediaTrackSettings & { displaySurface?: string }) | undefined;
  return s?.displaySurface === 'monitor' || s?.displaySurface === 'window' || s?.displaySurface === 'browser' ? s.displaySurface : null;
}

export type MediaErrorKind = 'denied' | 'not-found' | 'in-use' | 'insecure' | 'unsupported' | 'cancelled' | 'unknown';

export function classifyMediaError(err: unknown): MediaErrorKind {
  if (!window.isSecureContext) return 'insecure';
  if (!supports.media) return 'unsupported';
  const name = (err as DOMException)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'not-found';
  if (name === 'NotReadableError' || name === 'AbortError') return 'in-use';
  return 'unknown';
}

export type Platform = 'ios-safari' | 'ios-other' | 'android' | 'desktop';

export function detectPlatform(ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints): Platform {
  // iPadOS reports itself as a Mac; touch support gives it away.
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
  if (ios) return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) ? 'ios-other' : 'ios-safari';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/** Where to re-enable a blocked camera/microphone on this kind of device. */
export function permissionHelp(device: 'camera' | 'microphone', platform: Platform = detectPlatform()): string {
  const what = device === 'camera' ? 'Cámara' : 'Micrófono';
  switch (platform) {
    case 'ios-safari':
      return `En Safari toca «aA» en la barra de dirección → Configuración del sitio web → ${what} → Permitir. Si no aparece, revisa Ajustes del iPhone → Safari → ${what}. Después recarga la página.`;
    case 'ios-other':
      return `Abre Ajustes del iPhone → busca tu navegador (por ejemplo Chrome) → activa ${what}. Después vuelve y recarga la página.`;
    case 'android':
      return `Toca el icono a la izquierda de la dirección → Permisos → ${what} → Permitir. Si sigue bloqueado: Ajustes de Android → Aplicaciones → tu navegador → Permisos → ${what}.`;
    default:
      return `Haz clic en el icono a la izquierda de la dirección → ${what} → Permitir, y recarga la página. En macOS revisa también Ajustes del Sistema → Privacidad y seguridad → ${what}.`;
  }
}

export function describeMediaError(err: unknown, device: 'camera' | 'microphone' | 'screen'): string {
  const what = device === 'camera' ? 'la cámara' : device === 'microphone' ? 'el micrófono' : 'la pantalla';
  switch (classifyMediaError(err)) {
    case 'insecure':
      return `El navegador solo permite usar ${what} en conexiones seguras (HTTPS o localhost).`;
    case 'unsupported':
      return `Este navegador no permite acceder a ${what}.`;
    case 'denied':
      return device === 'screen'
        ? 'Has cancelado la selección o el sistema no permite compartir la pantalla.'
        : `No hay permiso para usar ${what}. ${permissionHelp(device)}`;
    case 'not-found':
      return `No se encontró ${what}. Conéctalo o elige otro dispositivo.`;
    case 'in-use':
      return `${what[0].toUpperCase()}${what.slice(1)} está ocupad${device === 'camera' || device === 'screen' ? 'a' : 'o'} por otra aplicación o pestaña. Ciérrala y vuelve a intentarlo.`;
    default:
      return `No se pudo acceder a ${what}.`;
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((t) => t.stop());
}
